import {
  L2_BUCKET,
  L2_CHUNKS_BUCKET,
  L3_BUCKET,
  getS3Object,
  listL2Prefix,
  listL3Prefix,
  listS3Dirs,
  listS3Fresh,
  listS3Prefix,
} from "../data/s3.js";
import { concatBytes } from "./binary.js";
import { L3_PRODUCT_SPECS } from "./quantize.js";
import { findClosestByTime, parseL3KeyTime, parseScanKey, parseScanTime } from "./sweeps.js";
import { decodeL2InWorker, decodeL2SeekStream, decodeL3InWorker, decodeStormInWorker, warmL2Worker } from "./workers.js";
import { idbGet, idbSet } from "../cache/idb.js";

function scanFromObject(obj) {
  const filename = obj.key.split("/").pop() || "";
  if (filename.includes("_MDM") || filename.startsWith(".")) return null;
  const time = parseScanTime(filename);
  if (!time) return null;
  return { time, key: obj.key, filename, size: obj.size };
}

export async function listL2Scans(stationId, dateClean, { signal } = {}) {
  const prefix = listL2Prefix(stationId, dateClean);
  const objects = await listS3Prefix(L2_BUCKET, prefix, { signal });
  const scans = objects.map(scanFromObject).filter(Boolean);
  scans.sort((a, b) => a.time.localeCompare(b.time));
  return { station: stationId, date: dateClean, count: scans.length, scans };
}

export async function listL3Products(stationId, dateClean, productCode, { signal } = {}) {
  const prefix = listL3Prefix(stationId, dateClean, productCode);
  const objects = await listS3Prefix(L3_BUCKET, prefix, { signal });
  const products = [];
  for (const obj of objects) {
    const time = parseL3KeyTime(obj.key);
    if (time) products.push({ key: obj.key, time, size: obj.size });
  }
  products.sort((a, b) => a.time.localeCompare(b.time));
  return products;
}

function asUint8(value) {
  if (!value) return null;
  if (value instanceof Uint8Array) return value;
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  if (ArrayBuffer.isView(value)) return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  return null;
}

async function getCachedBytes(key) {
  const rec = await idbGet(key);
  if (!rec?.value) return null;
  return asUint8(rec.value);
}

async function getCachedRaw(key) {
  const rec = await idbGet(key);
  if (!rec?.value) return null;
  const value = rec.value;
  if (value?.bytes) {
    return {
      bytes: asUint8(value.bytes),
      truncated: Boolean(value.truncated),
      contentLength: value.contentLength || 0,
    };
  }
  const bytes = asUint8(value);
  return bytes ? { bytes, truncated: false, contentLength: 0 } : null;
}

async function putCachedBytes(key, bytes) {
  await idbSet(key, bytes, bytes.byteLength);
}

async function putCachedRaw(key, bytes, { truncated = false, contentLength = 0 } = {}) {
  if (truncated) {
    await idbSet(key, { bytes, truncated: true, contentLength }, bytes.byteLength);
    return;
  }
  await putCachedBytes(key, bytes);
}

async function downloadVolume(bucket, key, { signal, onProgress } = {}) {
  const cacheKey = `raw:${bucket}:${key}`;
  const cached = await getCachedBytes(cacheKey);
  if (cached) return cached;
  const bytes = await getS3Object(bucket, key, { signal, onProgress });
  await putCachedBytes(cacheKey, bytes);
  return bytes;
}

function reviveFrame(frame) {
  if (!frame) return frame;
  if (Array.isArray(frame.data)) frame.data = Uint8Array.from(frame.data);
  else if (frame.data instanceof ArrayBuffer) frame.data = new Uint8Array(frame.data);
  return frame;
}

const L2_FRAME_CACHE_VERSION = 4;

function reviveCachedL2Payload(payload, includeSails) {
  if (!payload) return null;
  if (includeSails) {
    if (!Array.isArray(payload.frames) || !payload.frames.length) return null;
    payload.frames.forEach(reviveFrame);
    return payload;
  }
  if (payload.frames) return null;
  return reviveFrame(payload);
}

function cacheLater(key, value, bytes) {
  const store = () => {
    idbSet(key, value, bytes).catch(() => {});
  };
  if (typeof requestIdleCallback === "function") requestIdleCallback(store, { timeout: 2000 });
  else setTimeout(store, 0);
}

function downloadProgress(onProgress, size) {
  return (p) => {
    const total = p.total || size || p.loaded;
    onProgress?.({
      phase: "download",
      loaded: p.loaded,
      total,
      text: "Downloading…",
    });
  };
}

async function tryDecodeL2(bytes, decodeOpts, onProgress) {
  try {
    return await decodeL2InWorker(bytes, decodeOpts, onProgress);
  } catch {
    return null;
  }
}

async function downloadCompleteL2(key, { prefix, start = 0, objectSize, signal, onProgress } = {}) {
  try {
    const rest = await getS3Object(L2_BUCKET, key, {
      signal,
      start,
      onProgress: downloadProgress(onProgress, objectSize),
    });
    return start > 0 && prefix?.byteLength ? concatBytes(prefix, rest) : rest;
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    if (start > 0) {
      return getS3Object(L2_BUCKET, key, {
        signal,
        onProgress: downloadProgress(onProgress, objectSize),
      });
    }
    throw err;
  }
}

async function loadL2FullVolume({
  match,
  cached,
  objectSize,
  decodeOpts,
  signal,
  onProgress,
  rawKey,
}) {
  let bytes = cached?.bytes || null;
  const complete = Boolean(
    bytes
    && !cached?.truncated
    && (!objectSize || bytes.byteLength >= objectSize),
  );
  if (!complete) {
    onProgress?.({ phase: "download", text: "Downloading…" });
    const start = cached?.truncated && bytes?.byteLength ? bytes.byteLength : 0;
    bytes = await downloadCompleteL2(match.key, {
      prefix: bytes,
      start,
      objectSize,
      signal,
      onProgress,
    });
    await putCachedRaw(rawKey, bytes, {
      truncated: false,
      contentLength: objectSize || bytes.byteLength,
    });
  }
  const payload = await decodeL2InWorker(bytes, { ...decodeOpts, fullVolume: true }, onProgress);
  return { payload, storeBytes: bytes };
}

async function streamL2Volume({ key, prefix, start, objectSize, signal, onProgress, decodeOpts }) {
  let enough = false;
  const job = decodeL2SeekStream(decodeOpts, (p) => {
    if (p.enough) enough = true;
    onProgress?.(p);
  });
  const onAbort = () => {
    job.end();
  };
  signal?.addEventListener("abort", onAbort, { once: true });
  try {
    if (prefix?.byteLength) job.push(prefix);
    let downloaded = new Uint8Array(0);
    try {
      downloaded = await getS3Object(L2_BUCKET, key, {
        signal,
        start,
        onProgress: downloadProgress(onProgress, objectSize),
        onChunk(chunk) {
          job.push(chunk);
          return enough;
        },
      });
    } catch (err) {
      if (err?.name === "AbortError") {
        job.end();
        throw err;
      }
      if (start > 0) {
        job.end();
        job.promise.catch(() => {});
        const full = await getS3Object(L2_BUCKET, key, {
          signal,
          onProgress: downloadProgress(onProgress, objectSize),
        });
        const payload = await decodeL2InWorker(full, { ...decodeOpts, fullVolume: true }, onProgress);
        return { payload, storeBytes: full, truncated: false };
      }
      job.end();
      throw err;
    }
    if (!job.settled()) job.end();
    let payload;
    const combined = prefix?.byteLength ? concatBytes(prefix, downloaded) : downloaded;
    try {
      payload = await job.promise;
    } catch (err) {
      if (objectSize && combined.byteLength < objectSize) throw err;
      payload = await decodeL2InWorker(combined, { ...decodeOpts, fullVolume: true }, onProgress);
      return { payload, storeBytes: combined, truncated: false };
    }
    const truncated = objectSize ? combined.byteLength < objectSize : Boolean(payload.seek?.truncated);
    return { payload, storeBytes: combined, truncated };
  } finally {
    signal?.removeEventListener("abort", onAbort);
  }
}

export async function loadL2Polar({
  station,
  dateClean,
  timeClean,
  product,
  elevation = null,
  includeSails = false,
  s3Key = null,
  s3Size = 0,
  signal,
  onProgress,
} = {}) {
  const frameKey = `l2frame:v${L2_FRAME_CACHE_VERSION}:${station.id}:${dateClean}:${timeClean}:${product}:${elevation ?? "auto"}:${includeSails ? "sails" : "one"}`;
  const cachedPayload = reviveCachedL2Payload((await idbGet(frameKey))?.value, includeSails);
  if (cachedPayload) return cachedPayload;

  warmL2Worker();
  let match = s3Key
    ? { key: s3Key, size: s3Size || 0, time: timeClean }
    : null;
  if (!match) {
    onProgress?.({ phase: "listing", text: "Finding scan…" });
    const listed = await listL2Scans(station.id, dateClean, { signal });
    match = listed.scans.find((s) => s.time.replace(/:/g, "") === timeClean);
    if (!match) {
      const target = `${timeClean.slice(0, 2)}:${timeClean.slice(2, 4)}:${timeClean.slice(4, 6)}`;
      match = findClosestByTime(listed.scans, target, 24 * 3600);
    }
  }
  if (!match) throw new Error(`No radar data found for ${station.id} near ${dateClean} ${timeClean}`);

  const decodeOpts = {
    station,
    dateClean,
    timeClean,
    product,
    elevation,
    includeSails,
    s3Key: match.key,
  };
  const rawKey = `raw:${L2_BUCKET}:${match.key}`;
  const cached = await getCachedRaw(rawKey);
  const objectSize = match.size || cached?.contentLength || 0;

  onProgress?.({ phase: "download", text: "Downloading…" });

  let payload;
  let storeBytes = cached?.bytes || null;
  let truncated = Boolean(cached?.truncated);

  if (cached?.bytes?.byteLength) {
    const complete = !cached.truncated && (!objectSize || cached.bytes.byteLength >= objectSize);
    if (complete) {
      payload = await decodeL2InWorker(cached.bytes, decodeOpts, onProgress);
    } else {
      payload = await tryDecodeL2(cached.bytes, decodeOpts, onProgress);
      if (!payload) {
        const streamed = await streamL2Volume({
          key: match.key,
          prefix: cached.bytes,
          start: cached.bytes.byteLength,
          objectSize,
          signal,
          onProgress,
          decodeOpts,
        });
        payload = streamed.payload;
        storeBytes = streamed.storeBytes;
        truncated = streamed.truncated;
        await putCachedRaw(rawKey, storeBytes, { truncated, contentLength: objectSize });
      }
    }
  } else {
    const streamed = await streamL2Volume({
      key: match.key,
      prefix: null,
      start: 0,
      objectSize,
      signal,
      onProgress,
      decodeOpts,
    });
    payload = streamed.payload;
    storeBytes = streamed.storeBytes;
    truncated = streamed.truncated;
    await putCachedRaw(rawKey, storeBytes, { truncated, contentLength: objectSize });
  }

  cacheLater(frameKey, payload, storeBytes?.byteLength || objectSize || 0);
  return payload;
}

const CHUNK_VOLUME_MAX = 999;
const liveVolumeByStation = new Map(); // stationId -> { num, startMs }
const liveChunkBytes = new Map();      // chunk key -> Uint8Array (current volumes only)
const liveDecodeCache = new Map();     // decode key -> payload

export function parseChunkKey(key) {
  const match = String(key || "").match(/^([A-Z0-9]{4})\/(\d+)\/(\d{8})-(\d{6})-(\d{3})-([SIE])$/);
  if (!match) return null;
  const [, station, num, date, time, seq, kind] = match;
  return {
    station,
    num: Number(num),
    date,
    time,
    seq: Number(seq),
    kind,
    startMs: Date.UTC(
      Number(date.slice(0, 4)),
      Number(date.slice(4, 6)) - 1,
      Number(date.slice(6, 8)),
      Number(time.slice(0, 2)),
      Number(time.slice(2, 4)),
      Number(time.slice(4, 6)),
    ),
  };
}

async function listChunkVolume(stationId, num, { signal } = {}) {
  const objects = await listS3Fresh(L2_CHUNKS_BUCKET, `${stationId}/${num}/`, { signal });
  const chunks = objects
    .map((obj) => ({ ...parseChunkKey(obj.key), key: obj.key, size: obj.size }))
    .filter((c) => c.kind);
  chunks.sort((a, b) => a.seq - b.seq);
  // A leftover directory from ~999 volumes ago can share the number; keep one volume.
  const startMs = chunks.length ? Math.max(...chunks.map((c) => c.startMs)) : 0;
  const current = chunks.filter((c) => c.startMs === startMs);
  return { num, startMs, chunks: current, ended: current.some((c) => c.kind === "E") };
}

/**
 * Volume directories are numbered 1-999 and wrap, and old ones linger, so the
 * newest volume is the end of one of the contiguous runs. Probe each run end.
 */
async function discoverLiveVolume(stationId, { signal } = {}) {
  const dirs = await listS3Dirs(L2_CHUNKS_BUCKET, `${stationId}/`, { signal });
  const nums = new Set(
    dirs.map((d) => Number(d.split("/")[1])).filter((n) => Number.isInteger(n) && n > 0),
  );
  const runEnds = [...nums].filter((n) => !nums.has((n % CHUNK_VOLUME_MAX) + 1));
  const heads = await Promise.all(runEnds.map(async (num) => {
    const objects = await listS3Fresh(L2_CHUNKS_BUCKET, `${stationId}/${num}/`, { signal, maxKeys: 1 });
    const parsed = parseChunkKey(objects[0]?.key);
    return parsed ? { num, startMs: parsed.startMs } : null;
  }));
  let best = null;
  for (const head of heads) {
    if (head && (!best || head.startMs > best.startMs)) best = head;
  }
  return best ? listChunkVolume(stationId, best.num, { signal }) : null;
}

async function findLiveVolume(stationId, { signal } = {}) {
  const known = liveVolumeByStation.get(stationId);
  let volume = null;
  if (known) {
    volume = await listChunkVolume(stationId, known.num, { signal });
    if (!volume.chunks.length || volume.startMs < known.startMs) {
      volume = null;
    } else if (volume.ended) {
      const next = await listChunkVolume(stationId, (known.num % CHUNK_VOLUME_MAX) + 1, { signal });
      if (next.chunks.length && next.startMs > volume.startMs) volume = next;
    }
  }
  if (!volume) volume = await discoverLiveVolume(stationId, { signal });
  if (volume?.chunks.length) {
    liveVolumeByStation.set(stationId, { num: volume.num, startMs: volume.startMs });
  }
  return volume;
}

function pruneLiveCaches(keepPrefixes) {
  for (const key of liveChunkBytes.keys()) {
    if (!keepPrefixes.some((p) => key.startsWith(p))) liveChunkBytes.delete(key);
  }
  if (liveDecodeCache.size > 16) liveDecodeCache.clear();
}

/**
 * Complete lowest-tilt (or selected-tilt) sweeps from the volume the radar is
 * scanning right now, including SAILS cuts, from the real-time chunk bucket.
 * The archive bucket only posts a volume after it finishes, so without this a
 * live view is a whole volume behind and never shows a fresh SAILS pass.
 * Returns null unless that volume started after `afterMs` (the newest
 * archived volume), so frames never duplicate archived ones.
 */
export async function loadL2LiveFrames({
  station,
  product,
  elevation = null,
  afterMs = 0,
  signal,
} = {}) {
  const volume = await findLiveVolume(station.id, { signal });
  if (!volume?.chunks.length || volume.startMs <= afterMs) return null;
  if (volume.chunks[0].kind !== "S" || volume.chunks[0].seq !== 1) return null;

  // Chunks are sequential; stop at the first gap so the byte stream stays valid.
  const chunks = [];
  for (const c of volume.chunks) {
    if (c.seq !== chunks.length + 1) break;
    chunks.push(c);
  }
  const last = chunks[chunks.length - 1];
  const decodeKey = `${last.key}:${product}:${elevation ?? "auto"}`;
  if (liveDecodeCache.has(decodeKey)) return liveDecodeCache.get(decodeKey);

  const volumePrefix = `${station.id}/${volume.num}/`;
  pruneLiveCaches([volumePrefix]);
  const parts = await Promise.all(chunks.map(async (c) => {
    const hit = liveChunkBytes.get(c.key);
    if (hit) return hit;
    const bytes = await getS3Object(L2_CHUNKS_BUCKET, c.key, { signal });
    liveChunkBytes.set(c.key, bytes);
    return bytes;
  }));

  let payload;
  try {
    payload = await decodeL2InWorker(concatBytes(...parts), {
      station,
      dateClean: last.date,
      timeClean: last.time,
      product,
      elevation,
      includeSails: true,
      completeSweepsOnly: true,
      allowFallback: false,
      fullVolume: true,
      // Unique per chunk count so the worker's parsed-volume cache never
      // serves an older, shorter prefix of the same volume.
      s3Key: `${station.id}${last.date}_${last.time}_V06_live${volume.num}_${last.seq}`,
    });
  } catch {
    // Too early in the volume: no complete sweep at this tilt yet.
    payload = null;
  }
  const result = payload?.frames?.length
    ? { frames: payload.frames, volumeStartMs: volume.startMs, ended: volume.ended }
    : null;
  liveDecodeCache.set(decodeKey, result);
  return result;
}

export async function loadL3Polar({
  station,
  dateClean,
  timeClean,
  productKey,
  signal,
  onProgress,
} = {}) {
  const spec = L3_PRODUCT_SPECS[productKey];
  if (!spec) throw new Error(`Unknown Level 3 product: ${productKey}`);
  // v2: super-res gate spacing and hydrometeor class codes. Older entries
  // placed N0B/N0H several times too far out and painted the wrong precip type.
  const frameKey = `l3frame:v2:${station.id}:${dateClean}:${timeClean}:${productKey}`;
  const cachedFrame = await idbGet(frameKey);
  if (cachedFrame?.value) return reviveFrame(cachedFrame.value);

  onProgress?.({ phase: "listing", text: "Finding Level 3 product…" });
  const codes = [spec.code, ...(spec.fallback || [])];
  let products = [];
  let usedCode = spec.code;
  for (const code of codes) {
    products = await listL3Products(station.id, dateClean, code, { signal });
    if (products.length) {
      usedCode = code;
      break;
    }
  }
  const target = `${timeClean.slice(0, 2)}:${timeClean.slice(2, 4)}:${timeClean.slice(4, 6)}`;
  const closest = findClosestByTime(products, target, 600);
  if (!closest) throw new Error(`No Level 3 ${codes.join("/")} near ${dateClean} ${target}`);

  onProgress?.({ phase: "download", text: "Downloading Level 3…" });
  const raw = await downloadVolume(L3_BUCKET, closest.key, { signal, onProgress });
  onProgress?.({ phase: "decode", text: "Decoding Level 3…" });
  const rawSize = raw.byteLength;
  const payload = await decodeL3InWorker(raw, {
    station,
    dateClean,
    timeClean: closest.time.replace(/:/g, ""),
    productKey,
    timezone: station.timezone,
  });
  cacheLater(frameKey, payload, rawSize);
  return payload;
}

export async function loadStormTracks({ station, dateClean, timeClean, signal } = {}) {
  const cacheKey = `nst:${station.id}:${dateClean}:${timeClean}`;
  const cached = await idbGet(cacheKey);
  if (cached?.value) return cached.value;

  const timeFmt = `${timeClean.slice(0, 2)}:${timeClean.slice(2, 4)}:${timeClean.slice(4, 6)}`;
  const nstList = await listL3Products(station.id, dateClean, "NST", { signal });
  const closest = findClosestByTime(nstList, timeFmt, 600);
  if (!closest) {
    const empty = { station: station.id, date: dateClean, time: timeFmt, nst_time: null, num_storms: 0, storms: [] };
    return empty;
  }
  const nstBytes = await downloadVolume(L3_BUCKET, closest.key, { signal });
  const nst = await decodeStormInWorker(nstBytes, station);

  const ntvList = await listL3Products(station.id, dateClean, "NTV", { signal });
  const ntvClosest = findClosestByTime(ntvList, timeFmt, 600);
  let tvs = {};
  if (ntvClosest) {
    try {
      const ntvBytes = await downloadVolume(L3_BUCKET, ntvClosest.key, { signal });
      const ntv = await decodeStormInWorker(ntvBytes, station);
      tvs = ntv.tvs || {};
    } catch {
      tvs = {};
    }
  }

  const storms = Object.values(nst.storms || {}).map((storm) => ({
    ...storm,
    tvs: !!(tvs[storm.id] && tvs[storm.id].tvs),
  }));
  const result = {
    station: station.id,
    date: dateClean,
    time: timeFmt,
    nst_time: closest.time,
    num_storms: storms.length,
    storms,
  };
  await idbSet(cacheKey, result, 4096);
  return result;
}

export function isL3Product(product) {
  return typeof product === "string" && product.startsWith("l3_");
}

export { parseScanKey };
